import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
} from "react";
import { usePostHog } from "posthog-js/react";

type AuthTokens = {
  accessToken: string;
  refreshToken: string;
  expiresIn: number; // в секундах
};

type AuthContextType = {
  accessToken: string | null;
  login: (code: string) => Promise<void>;
  logout: () => void;
  getAccessToken: () => Promise<string | null>;
  expiresAt: any;
};

const clientId = process.env.NEXT_PUBLIC_STOREFRONT_CLIENT_ID;

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const posthog = usePostHog();
  const [expiresAt, setExpiresAt] = useState(null);
  const refreshInProgress = useRef(null);
  const login = async (code: string) => {
    try {
      const body = new URLSearchParams();
      body.append("grant_type", "authorization_code");
      body.append("client_id", clientId);
      body.append(
        "redirect_uri",
        `${window ? window.location.origin : ""}/account`
      );
      body.append("code", code);
      const codeVerifier = localStorage.getItem("code-verifier");
      body.append("code_verifier", codeVerifier);
      const headers = {
        "content-type": "application/x-www-form-urlencoded",
      };
      const response = await fetch(
        `https://shopify.com/authentication/10206689/oauth/token`,
        {
          method: "POST",
          headers: headers,
          body,
        }
      );
      const data = await response.json();
      const dateExpired = new Date();
      dateExpired.setHours(dateExpired.getHours() + data.expires_in / 60 / 60);
      setAccessToken(data.access_token);
      setIdToken(data.id_token);
      setExpiresAt(+dateExpired);
      localStorage.setItem("refreshToken", data.refresh_token);
    } catch (error) {
      logout();
    }
  };

  const logout = () => {
    setAccessToken(null);
    setExpiresAt(null);
    localStorage.removeItem("refreshToken");
    posthog.reset();
    if (idToken)
      window.location.href = `https://shopify.com/10206689/auth/logout?id_token_hint=${idToken}`;
    document.cookie = `refresh=; Max-Age=0`;
  };

  const refreshAccessToken = async (): Promise<string | null> => {
    if (refreshInProgress.current) return refreshInProgress.current;

    const refreshToken = localStorage.getItem("refreshToken");
    if (!refreshToken) {
      return null;
    }

    try {
      const body = new URLSearchParams();
      body.append("grant_type", "refresh_token");
      body.append("client_id", clientId);
      body.append("refresh_token", refreshToken);
      const headers = {
        "content-type": "application/x-www-form-urlencoded",
      };
      refreshInProgress.current = fetch(
        `https://shopify.com/authentication/10206689/oauth/token`,
        {
          method: "POST",
          headers: headers,
          body,
        }
      )
        .then(async (res) => {
          if (!res.ok) throw new Error("Failed to refresh token");
          const data = await res.json();
          if (data.error || data.error_description) throw new Error("error");
          const dateExpired = new Date();
          dateExpired.setHours(
            dateExpired.getHours() + data.expires_in / 60 / 60
          );
          setAccessToken(data.access_token);
          setIdToken(data.id_token);
          setExpiresAt(+dateExpired);
          localStorage.setItem("refreshToken", data.refresh_token);
          return data.access_token;
        })
        .catch((err) => {
          console.error("Token refresh error:", err);
          logout();
          return null;
        })
        .finally(() => {
          refreshInProgress.current = null;
        });

      return refreshInProgress.current;
    } catch (e) {
      console.log(e);
      logout();
    }
  };

  const getAccessToken = async () => {
    if (!accessToken || !expiresAt || new Date() > new Date(expiresAt)) {
      return await refreshAccessToken();
    }
    return accessToken;
  };

  useEffect(() => {
    const init = async () => {
      const refreshToken = localStorage.getItem("refreshToken");
      if (refreshToken && !accessToken) {
        await refreshAccessToken();
      }
    };
    init();
  }, []);

  return (
    <AuthContext.Provider
      value={{ accessToken, login, logout, getAccessToken, expiresAt }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
};
